import { useCallback, useEffect, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import Container from '@mui/material/Container'
import { alpha } from '@mui/material/styles'

/** The height of the sticky header, which everything here has to sit under. */
const HEADER_H = { xs: 60, md: 72 }

export interface SectionMark {
  /** The `id` on the section this chip scrolls to. */
  id: string
  emoji: string
  label: string
}

/**
 * The row of pills under the header: one tap to any part of a long page.
 *
 * WHY THESE SCROLL RATHER THAN NAVIGATE
 * -------------------------------------
 * GeoSmile's rail is the same shape and does the same job, but it is one long page, so its
 * chips jump to sections of it. Here the home page is also long - five sections, each a grid
 * of cards - and had no way through it except the scrollbar. The chips are for that.
 *
 * They deliberately do NOT duplicate the menu. A chip and a menu item that read the same but
 * go to different places is the kind of thing that teaches people not to trust either.
 *
 * WHY IT STICKS
 * -------------
 * Position sticky, not a second copy mounted on scroll. One element means one set of chips in
 * the accessibility tree and no chance of the two drifting apart. The sentinel above it exists
 * only to notice when it has stuck, so the strip can change from the page colour to white with
 * a line under it - the same change GeoSmile makes, and for the same reason: once it is
 * floating over content it needs an edge or the chips appear to hover in mid-air.
 */
export function SectionChips({ marks }: { marks: SectionMark[] }) {
  const sentinel = useRef<HTMLDivElement | null>(null)
  const [pinned, setPinned] = useState(false)

  useEffect(() => {
    const node = sentinel.current
    if (!node) return
    const observer = new IntersectionObserver(
      ([entry]) => setPinned(!entry.isIntersecting),
      // The sentinel sits above the rail; once it is out of sight behind the header, the rail
      // has reached its sticky position.
      { rootMargin: '-80px 0px 0px 0px', threshold: 0 },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  const jump = useCallback((id: string) => {
    const target = document.getElementById(id)
    if (!target) return
    // Measured, not hardcoded: the header is 60px on a phone and 72px on a desktop, and the
    // rail itself is in the way once it has stuck. Reading both elements means the section
    // heading lands just below them whatever the breakpoint.
    const header = document.querySelector('header')?.getBoundingClientRect().height ?? 0
    const rail = document.getElementById('section-chips')?.getBoundingClientRect().height ?? 0
    const top = target.getBoundingClientRect().top + window.scrollY - header - rail - 8
    window.scrollTo({ top, behavior: 'smooth' })
  }, [])

  return (
    <>
      <Box ref={sentinel} aria-hidden sx={{ height: 0 }} />
      <Box
        id="section-chips"
        component="nav"
        sx={{
          position: 'sticky',
          top: HEADER_H,
          zIndex: (theme) => theme.zIndex.appBar - 1,
          py: 1.5,
          bgcolor: pinned ? 'background.paper' : 'background.default',
          borderBottom: pinned ? 1 : 0,
          borderColor: 'divider',
          transition: 'background-color 160ms, box-shadow 160ms',
          boxShadow: pinned ? '0 4px 14px rgba(11, 110, 122, 0.06)' : 'none',
        }}
      >
        <Container
          sx={{
            display: 'flex',
            gap: 1,
            justifyContent: { xs: 'flex-start', md: 'center' },
            flexWrap: { xs: 'nowrap', md: 'wrap' },
            rowGap: 1,
            // Off the edge on a phone. Five chips wrapped onto three lines is a wall, and a
            // sideways scroll reads as "there is more here".
            overflowX: { xs: 'auto', md: 'visible' },
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {marks.map((mark) => (
            <ButtonBase
              key={mark.id}
              onClick={() => jump(mark.id)}
              sx={{
                flex: '0 0 auto',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.9,
                px: 1.9,
                py: 1.15,
                borderRadius: 999,
                bgcolor: 'background.paper',
                border: 1,
                borderColor: 'divider',
                fontSize: 14,
                fontWeight: 600,
                color: 'text.primary',
                lineHeight: 1,
                transition: 'background-color 140ms, border-color 140ms',
                '&:hover': {
                  // A tint of the brand colour rather than a grey: the chip should look
                  // like it belongs to this site when you touch it.
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                  borderColor: 'primary.main',
                },
                '&:focus-visible': {
                  outline: 2,
                  outlineColor: 'primary.main',
                  outlineOffset: 2,
                },
              }}
            >
              <Box component="span" aria-hidden sx={{ fontSize: 15, lineHeight: 1 }}>
                {mark.emoji}
              </Box>
              {mark.label}
            </ButtonBase>
          ))}
        </Container>
      </Box>
    </>
  )
}
